import { useEffect } from "react";

/**
 * SeoHead — CMS-driven SEO manager for the public landing page.
 *
 * Sets: document.title, meta description/keywords/author/robots/theme-color/geo,
 * Open Graph tags (og:*), Twitter Card tags, canonical link, favicon, html lang,
 * and JSON-LD structured data (Organization + LocalBusiness + FAQ + BreadcrumbList).
 * All values are pulled live from the CMS `seo` block so admin edits reflect instantly.
 */
export default function SeoHead({ content }) {
  useEffect(() => {
    if (!content) return;
    const seo = content.seo || {};
    const brand = content.brand || {};
    const contact = content.contact || {};
    const faqs = content.faq || [];

    // Title
    if (seo.title) document.title = seo.title;

    // <html lang>
    if (seo.language) document.documentElement.setAttribute("lang", seo.language);

    // Basic metas
    setMeta("description", seo.description);
    setMeta("keywords", seo.keywords);
    setMeta("author", seo.author);
    setMeta("robots", seo.robots || "index, follow");
    setMeta("theme-color", seo.theme_color);
    setMeta("geo.region", seo.geo_region);
    setMeta("geo.placename", seo.geo_placename);

    // Open Graph
    setMeta("og:title", seo.title, "property");
    setMeta("og:description", seo.description, "property");
    setMeta("og:type", seo.og_type || "website", "property");
    setMeta("og:url", seo.canonical_url, "property");
    setMeta("og:image", seo.og_image_url, "property");
    setMeta("og:site_name", brand.name, "property");
    setMeta("og:locale", (seo.language || "en_IN").replace("-", "_"), "property");

    // Twitter
    setMeta("twitter:card", seo.twitter_card || "summary_large_image");
    setMeta("twitter:site", seo.twitter_handle);
    setMeta("twitter:title", seo.title);
    setMeta("twitter:description", seo.description);
    setMeta("twitter:image", seo.og_image_url);

    // Canonical
    setLink("canonical", seo.canonical_url);

    // Favicon
    if (seo.favicon_url) setLink("icon", seo.favicon_url);

    // JSON-LD structured data
    if (seo.structured_data_enabled !== false) {
      const graph = [
        buildOrganization(brand, contact, seo, content),
        buildLocalBusiness(brand, contact, seo, content),
        faqs.length ? buildFAQ(faqs) : null,
        buildWebSite(brand, seo),
      ].filter(Boolean);
      setJsonLd("seo-jsonld-graph", { "@context": "https://schema.org", "@graph": graph });
    } else {
      removeJsonLd("seo-jsonld-graph");
    }

    return () => {
      // Keep tags on unmount — most sites want them cached; JSON-LD is idempotent via id.
    };
  }, [content]);

  return null;
}

/* ─────────── helpers ─────────── */

function setMeta(name, value, attr = "name") {
  if (typeof document === "undefined") return;
  if (value === undefined || value === null || value === "") {
    const existing = document.head.querySelector(`meta[${attr}="${name}"][data-seo="1"]`);
    if (existing) existing.remove();
    return;
  }
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(value));
}

function setLink(rel, href) {
  if (typeof document === "undefined") return;
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, obj) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(obj);
}

function removeJsonLd(id) {
  if (typeof document === "undefined") return;
  const el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (el) el.remove();
}

/* ─────────── JSON-LD builders ─────────── */

function buildOrganization(brand, contact, seo, content) {
  const org = {
    "@type": "Organization",
    name: brand.name || "FuelExpress Solutions",
    url: seo.canonical_url || undefined,
    logo: seo.og_image_url || brand.logo_url || undefined,
    description: seo.description,
    sameAs: [
      content.social?.facebook,
      content.social?.twitter,
      content.social?.linkedin,
      content.social?.instagram,
    ].filter(Boolean),
  };
  if (contact.phone || contact.email) {
    org.contactPoint = {
      "@type": "ContactPoint",
      telephone: contact.phone,
      email: contact.email,
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    };
  }
  if (contact.address) {
    org.address = {
      "@type": "PostalAddress",
      streetAddress: contact.address,
      addressCountry: "IN",
    };
  }
  return org;
}

function buildLocalBusiness(brand, contact, seo, content) {
  const cities = content.coverage?.cities || [];
  return {
    "@type": "LocalBusiness",
    name: brand.name || "FuelExpress Solutions",
    image: seo.og_image_url || undefined,
    telephone: contact.phone,
    email: contact.email,
    priceRange: "₹₹",
    url: seo.canonical_url,
    description: seo.description,
    address: contact.address
      ? {
          "@type": "PostalAddress",
          streetAddress: contact.address,
          addressCountry: "IN",
        }
      : undefined,
    areaServed: cities.map((c) => ({ "@type": "City", name: c })),
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  };
}

function buildFAQ(items) {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };
}

function buildWebSite(brand, seo) {
  return {
    "@type": "WebSite",
    name: brand.name || "FuelExpress Solutions",
    url: seo.canonical_url,
    inLanguage: seo.language || "en-IN",
    publisher: { "@type": "Organization", name: brand.name || "FuelExpress Solutions" },
  };
}
